import * as React from 'react'
import { v4 as uuid } from 'uuid'

import dynamic from 'next/dynamic'
import withHydrationOnDemand from 'react-hydration-on-demand'
import withoutHydration from 'react-without-hydration'

import { useLogger } from '@thg-commerce/enterprise-core'

import {
  PostContent,
  ElementNode,
  WidgetNode,
  ContainerNode,
  ListElementNode,
  NodeChildren,
} from '../../../post-content'
import {
  StyledGrid,
  StyledImage,
  WidgetGridItem,
  ElementGridItem,
  RecipeGridItem,
} from './styles'
import { RendererContext } from './types'
import { Link, Header, List, Text, Table } from './elements'
import { validateSkus } from '../ProductRecommendationsRenderer'
import { buildImageSrc } from '../../utils'

const ProductRecommendationsRenderer = dynamic<any>(() =>
  import('../ProductRecommendationsRenderer').then(
    (mod) => mod.ProductRecommendationsRenderer,
  ),
)
const TopNWidgetRenderer = dynamic<any>(() =>
  import('../TopNWidgetRenderer').then((mod) => mod.TopNWidgetRenderer),
)
const ImageCarouselRenderer = dynamic<any>(() =>
  import('../ImageCarouselRenderer').then((mod) => mod.ImageCarouselRenderer),
)
const GlobalLinkCards = dynamic<any>(() =>
  import('@thg-commerce/enterprise-widget-global-link-cards').then(
    (mod) => mod.GlobalLinkCards,
  ),
)
const SocialEmbed = dynamic<any>(() =>
  import('@thg-commerce/enterprise-widget-social-embed').then(
    (mod) => mod.SocialEmbed,
  ),
)
const ProductGridRenderer = dynamic<any>(() =>
  import('../ProductGridRenderer').then((mod) => mod.ProductGridRenderer),
)
const QuestionAndAnswer = dynamic<any>(() =>
  import('@thg-commerce/enterprise-widget-question-answer').then(
    (mod) => mod.QuestionAndAnswer,
  ),
)
const PostCardRenderer = dynamic<any>(() =>
  import('../PostCardRenderer').then((mod) => mod.PostCardRenderer),
)
const TopNItemRenderer = dynamic<any>(() =>
  import('../TopNItemRenderer').then((mod) => mod.TopNItemRenderer),
)
const TutorialRenderer = dynamic<any>(() =>
  import('../TutorialRenderer').then((mod) => mod.TutorialRenderer),
)
const Figure = dynamic<any>(() =>
  import('@thg-commerce/gravity-elements/Figure').then((mod) => mod.Figure),
)
const HighlightBox = dynamic<any>(() =>
  import('@thg-commerce/enterprise-widget-highlight-box').then(
    (mod) => mod.HighlightBox,
  ),
)
const Recipe = dynamic<any>(() =>
  import('@thg-commerce/enterprise-widget-recipe').then((mod) => mod.Recipe),
)
const MacroCalculator = dynamic<any>(() =>
  import('@thg-commerce/enterprise-widget-macro-calculator').then(
    (mod) => mod.MacroCalculator,
  ),
)
const Disclaimer = dynamic<any>(() =>
  import('@thg-commerce/enterprise-widget-disclaimer').then(
    (mod) => mod.Disclaimer,
  ),
)
const ButtonCollection = dynamic<any>(() =>
  import('@thg-commerce/enterprise-widget-button-collection').then(
    (mod) => mod.ButtonCollection,
  ),
)
const Quote = dynamic<any>(() =>
  import('@thg-commerce/enterprise-widget-quote').then((mod) => mod.Quote),
)

const ProductRecommendationsRendererComponent = withHydrationOnDemand({
  on: ['visible'],
})(ProductRecommendationsRenderer)
const TopNWidgetRendererComponent = withHydrationOnDemand({
  on: ['visible'],
})(TopNWidgetRenderer)
const ImageCarouselRendererComponent = withHydrationOnDemand({
  on: ['visible'],
})(ImageCarouselRenderer)
const GlobalLinkCardsComponent = withoutHydration()(GlobalLinkCards)
const SocialEmbedComponent = withHydrationOnDemand({
  on: ['visible'],
})(SocialEmbed)
const ProductGridRendererComponent = withHydrationOnDemand({
  on: ['visible'],
})(ProductGridRenderer)
const QuestionAndAnswerComponent = withoutHydration()(QuestionAndAnswer)
const PostCardRendererComponent = withHydrationOnDemand({
  on: ['visible'],
})(PostCardRenderer)
const TopNItemRendererComponent = withoutHydration()(TopNItemRenderer)
const TutorialRendererComponent = withoutHydration()(TutorialRenderer)
const FigureComponent = withoutHydration()(Figure)
const HighlightBoxComponent = withHydrationOnDemand({
  on: ['visible'],
})(HighlightBox)
const RecipeComponent = withoutHydration()(Recipe)
const MacroCalculatorComponent = withHydrationOnDemand({
  on: ['visible'],
})(MacroCalculator)
const DisclaimerComponent = withoutHydration()(Disclaimer)
const ButtonCollectionComponent = withoutHydration()(ButtonCollection)
const QuoteComponent = withoutHydration()(Quote)

interface ArticleBodyRendererProps {
  content: PostContent
  isAmp: boolean
  insetGridItem: {
    colStart: number | number[]
    colSpan: number | number[]
  }
}

const LOG_PREFIX = 'ArticleBodyRenderer: '

const renderElementChildren = (node: ElementNode, context: RendererContext) => {
  if ('children' in node && node.children) {
    return nodeRenderer(node.children, context)
  }
  if (node.props && 'text' in node.props) {
    return node.props.text
  }
  return null
}

const elementTagMap = {
  link: 'a',
}

const elementComponentMap = {
  image: StyledImage,
  link: Link,
  header: Header,
  list: ({
    type,
    items,
    context,
  }: ListElementNode['props'] & {
    context: RendererContext
  }) => List({ type, items, context, nodeRenderer }),
  text: Text,
  table: Table,
}

type OmitDistributive<T, K extends PropertyKey> = T extends any
  ? T extends object
    ? Id<OmitRecursively<T, K>>
    : T
  : never
type Id<T> = {} & { [P in keyof T]: T[P] }
type OmitRecursively<T extends any, K extends PropertyKey> = Omit<
  { [P in keyof T]: OmitDistributive<T[P], K> },
  K
>

const elementNodeRenderer = (
  node: ElementNode,
  context: RendererContext,
  index: number,
) => {
  const elementType =
    elementComponentMap[node.type] || elementTagMap[node.type] || node.type
  let text: string | null = null
  let props: ElementNode['props'] = node.props

  if (props && 'src' in props) {
    props.src[0].url = buildImageSrc(props.src[0].url, 700)
  }

  if (node.props && 'text' in node.props) {
    const { text: elementText = null, ...propsWithoutText } = node.props
    text = elementText
    props = propsWithoutText as OmitRecursively<ElementNode['props'], 'text'>
  }

  return React.createElement<
    ElementNode['props'] & { context?: RendererContext; isAmp?: boolean }
  >(
    elementType,
    {
      key: node.type === 'link' ? uuid() : index,
      ...props,
      ...(elementComponentMap[node.type]
        ? { context, isAmp: context.isAmp }
        : {}),
    },
    text ? text : renderElementChildren(node, context),
  )
}

type WidgetDiscriminateUnion<
  Type,
  Key extends keyof Type,
  Value extends Type[Key]
> = Type extends Record<Key, Value> ? Type : never
type WidgetMapDiscriminatedUnion<
  Type extends Record<Key, string>,
  Key extends keyof Type,
  ValueKey extends keyof Type
> = {
  [Value in Type[Key]]: {
    component:
      | React.ComponentType<
          WidgetDiscriminateUnion<Type, Key, Value>[ValueKey] & {
            isAmp: boolean
          }
        >
      | React.ComponentType<WidgetDiscriminateUnion<Type, Key, Value>[ValueKey]>
    shouldRender?: (props: WidgetNode['props']) => boolean
  }
}

const widgetMap: WidgetMapDiscriminatedUnion<WidgetNode, 'type', 'props'> = {
  productRecommendations: {
    component: ProductRecommendationsRendererComponent,
    shouldRender: validateSkus,
  },
  topN: { component: TopNWidgetRendererComponent },
  imageCarousel: { component: ImageCarouselRendererComponent },
  globalLinkCards: { component: GlobalLinkCardsComponent },
  media: { component: SocialEmbedComponent },
  productGrid: { component: ProductGridRendererComponent },
  qa: { component: QuestionAndAnswerComponent },
  postCard: { component: PostCardRendererComponent },
  topNItem: { component: TopNItemRendererComponent },
  tutorial: { component: TutorialRendererComponent },
  figure: { component: FigureComponent },
  highlightBox: { component: HighlightBoxComponent },
  recipeDetails: { component: RecipeComponent },
  recipeIngredients: { component: RecipeComponent },
  recipeInstructions: { component: RecipeComponent },
  recipeNutrition: { component: RecipeComponent },
  macronutrientCalculator: { component: MacroCalculatorComponent },
  disclaimer: { component: DisclaimerComponent },
  button: { component: ButtonCollectionComponent },
  quote: { component: QuoteComponent },
}

const widgetNodeRenderer = (
  node: WidgetNode,
  context: RendererContext,
  index: number,
) => {
  if (!widgetMap[node.type]) {
    context.logger.warn(`${LOG_PREFIX}Unsupported widget type ${node.type}`)
    return null
  }

  const shouldRender = widgetMap[node.type].shouldRender
  if (shouldRender ? !shouldRender(node.props) : false) return null

  const widgetChildren =
    node.props && 'children' in node.props
      ? nodeRenderer(node.props.children, context)
      : {}

  return React.createElement<{ isAmp: boolean } & any>(
    widgetMap[node.type].component,
    {
      ...node.props,
      isAmp: context.isAmp,
      key: index,
    },
    widgetChildren,
  )
}

const containerNodeRenderer = (
  node: ContainerNode,
  context: RendererContext,
) => {
  return nodeRenderer(node.children, context)
}

type FindByNodeType<Union, Node> = Union extends { node: Node } ? Union : never
type RenderNodeFunction<Node> = (
  node: FindByNodeType<NodeChildren, Node>,
  context: RendererContext,
  index: number,
) => React.ReactNode

const nodeRendererMap: {
  [Node in NodeChildren['node']]: RenderNodeFunction<Node>
} = {
  element: elementNodeRenderer,
  widget: widgetNodeRenderer,
  container: containerNodeRenderer,
}

type TypeConfig = {
  fullWidth: boolean
}

const widgetTypeConfig: { [type: string]: TypeConfig } = {
  productRecommendations: {
    fullWidth: true,
  },
  globalLinkCards: {
    fullWidth: true,
  },
  productGrid: {
    fullWidth: true,
  },
}

const defaultTypeConfig: TypeConfig = {
  fullWidth: false,
}

const nodeRenderer = (
  nodes: NodeChildren[],
  context: RendererContext,
  gridItem?: {
    colStart: number | number[]
    colSpan: number | number[]
  },
): React.ReactNode[] => {
  let nodeBuffer: React.ReactNode[] = []
  const nodesToRender: React.ReactNode[] = []
  let rowCount: number = 0
  nodes.forEach((node, index) => {
    const { node: nodeRendererType } = node
    const lastNode = index >= nodes.length - 1 ? true : false

    if (!nodeRendererMap[nodeRendererType]) {
      context.logger.warn(
        `${LOG_PREFIX}Unsupported node renderer ${nodeRendererType}`,
      )
      return
    }

    const renderFunction = nodeRendererMap[
      nodeRendererType
    ] as RenderNodeFunction<typeof nodeRendererType>

    const nodeElement = renderFunction(node, context, index)

    const wrapWithStyledGrid = (
      nodeArray: React.ReactNode[],
      isElement: boolean,
    ) => {
      rowCount += 1
      const typeConfig =
        !isElement && 'type' in node && node.type && widgetTypeConfig[node.type]
          ? widgetTypeConfig[node.type]
          : defaultTypeConfig

      const isRecipeWidget =
        'type' in node &&
        (node.type === 'recipeDetails' ||
          node.type === 'recipeIngredients' ||
          node.type === 'recipeInstructions' ||
          node.type === 'recipeNutrition')

      const StyledGridItem = isElement
        ? ElementGridItem
        : isRecipeWidget
        ? RecipeGridItem
        : WidgetGridItem

      return gridItem ? (
        <StyledGridItem
          key={rowCount}
          colStart={typeConfig.fullWidth ? 1 : gridItem?.colStart}
          colSpan={typeConfig.fullWidth ? 12 : gridItem?.colSpan}
          rowStart={rowCount}
        >
          {nodeArray}
        </StyledGridItem>
      ) : (
        <React.Fragment key={rowCount}>{nodeArray}</React.Fragment>
      )
    }

    switch (nodeRendererType) {
      case 'element':
        nodeBuffer.push(nodeElement)
        lastNode && nodesToRender.push(wrapWithStyledGrid(nodeBuffer, true))
        break

      case 'container':
        nodeBuffer.push(nodeElement)
        lastNode && nodesToRender.push(wrapWithStyledGrid(nodeBuffer, true))
        break

      case 'widget':
        let element: React.ReactNode | undefined

        if (nodeBuffer.length > 0) {
          element = wrapWithStyledGrid(nodeBuffer, true)
        }

        const widget: React.ReactNode =
          nodeElement && wrapWithStyledGrid([nodeElement], false)
        element !== undefined
          ? nodesToRender.push(element, widget)
          : nodesToRender.push(widget)
        nodeBuffer = []
        break
    }
  })
  return nodesToRender
}

export const ArticleBodyRenderer = ({
  isAmp,
  content: { content },
  insetGridItem,
}: ArticleBodyRendererProps) => {
  const logger = useLogger()
  return (
    <StyledGrid>
      {nodeRenderer(
        content,
        {
          isAmp,
          logger,
        },
        insetGridItem,
      )}
    </StyledGrid>
  )
}
